import React, { useEffect, useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import { resolvePublicUrl } from '@core/utils/assetFolderSlug';
import { openGameLogs } from '@core/utils/gameLogsUrl';
import { getLobbyUrlFromGameQuery } from '@config/runtime.config';
import { SKYX_NEW_BET_HISTORY_LOGS_BUTTON } from './SkyxNewBetHistoryLogsButton.config';
import {
  buildSkyxMenuAtlasFrameStyle,
  resolveSkyxMenuAtlasImageUrl,
  type SkyxMenuAtlasJson,
} from './skyxMenuAtlasFrame';

export interface SkyxNewBetHistoryLogsButtonProps {
  ariaLabel: string;
  buttonStyle?: CSSProperties;
  iconStyle?: CSSProperties;
  className?: string;
}

type LogsIconVisualState = 'normal' | 'hover' | 'down';

export const SkyxNewBetHistoryLogsButton: React.FC<
  SkyxNewBetHistoryLogsButtonProps
> = ({ ariaLabel, buttonStyle, iconStyle, className }) => {
  const { atlasJson, frames, iconSizePx } = SKYX_NEW_BET_HISTORY_LOGS_BUTTON;
  const atlasJsonUrl = useMemo(
    () => resolvePublicUrl(`/games/skyx_new/${atlasJson}`),
    [atlasJson]
  );

  const [atlas, setAtlas] = useState<SkyxMenuAtlasJson | null>(null);
  const [visualState, setVisualState] = useState<LogsIconVisualState>('normal');

  useEffect(() => {
    let cancelled = false;

    fetch(atlasJsonUrl)
      .then(res => {
        if (!res.ok) {
          throw new Error(`Atlas fetch failed: ${res.status}`);
        }
        return res.json() as Promise<SkyxMenuAtlasJson>;
      })
      .then(data => {
        if (!cancelled) {
          setAtlas(data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setAtlas(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [atlasJsonUrl]);

  const atlasImageUrl = useMemo(() => {
    if (!atlas) {
      return null;
    }
    return resolveSkyxMenuAtlasImageUrl(atlasJsonUrl, atlas.meta.image);
  }, [atlas, atlasJsonUrl]);

  const activeFrameName = useMemo(() => {
    if (visualState === 'down') {
      return frames.down;
    }
    if (visualState === 'hover') {
      return frames.hover;
    }
    return frames.normal;
  }, [frames.down, frames.hover, frames.normal, visualState]);

  const spriteStyle = useMemo(() => {
    if (!atlas || !atlasImageUrl) {
      return null;
    }
    return (
      buildSkyxMenuAtlasFrameStyle(
        atlas,
        activeFrameName,
        atlasImageUrl,
        iconSizePx
      ) ??
      buildSkyxMenuAtlasFrameStyle(
        atlas,
        frames.normal,
        atlasImageUrl,
        iconSizePx
      )
    );
  }, [activeFrameName, atlas, atlasImageUrl, frames.normal, iconSizePx]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    openGameLogs({ lobbyUrl: getLobbyUrlFromGameQuery() });
  };

  if (!spriteStyle) {
    return null;
  }

  return (
    <button
      type="button"
      className={className}
      style={buttonStyle}
      onClick={handleClick}
      aria-label={ariaLabel}
      onMouseEnter={() => setVisualState('hover')}
      onMouseLeave={() => setVisualState('normal')}
      onMouseDown={() => setVisualState('down')}
      onMouseUp={() => setVisualState('hover')}
    >
      <span
        aria-hidden
        style={{
          ...spriteStyle,
          filter: 'brightness(0) invert(1)',
          ...iconStyle,
        }}
      />
    </button>
  );
};
